import { useEffect, useRef, useState } from "react"
import { useAuth } from "@/lib/auth"
import { Shell } from "@/components/Shell"
import { MacroProgress } from "@/components/MacroProgress"
import { MealComposer } from "@/components/MealComposer"
import {
  deleteMeal,
  getMealsByDate,
  listRecipes,
  patchMeal,
  type MealRow,
  type MealsByDate,
} from "@/lib/api"
import { formatDateRelative, localToday } from "@/lib/dates"

/**
 * Today — the everyday surface. Top: live macro progress bars vs targets.
 * Middle: quick-log form. Bottom: today's meals grouped by type.
 */
export function TodayPage() {
  const auth = useAuth()
  const [data, setData] = useState<MealsByDate | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [backfillNote, setBackfillNote] = useState<string | null>(null)
  // Drives the empty-state value-prop: when both meals AND saved recipes
  // are zero, we show the "how this app is different" card instead of a
  // bare "nothing logged" line. null = still loading.
  const [cookbookCount, setCookbookCount] = useState<number | null>(null)

  // Local date, not UTC: otherwise evening entries appear to vanish the
  // moment UTC midnight passes (bug-bash M11).
  const today = localToday()

  const load = async () => {
    try {
      setData(await getMealsByDate(today))
      setErr(null)
    } catch (e: any) {
      setErr(e?.message ?? "Load failed")
    } finally {
      setLoading(false)
    }
  }

  // If the user logged for a past day, the meal won't appear in today's
  // list — surface a transient note so the action doesn't feel silent.
  const onLogged = (loggedDate: string) => {
    if (loggedDate !== today) {
      setBackfillNote(`✓ logged to ${formatDateRelative(loggedDate)}`)
      window.setTimeout(() => setBackfillNote(null), 4000)
    } else {
      setBackfillNote(null)
    }
    void load()
  }

  useEffect(() => {
    void load()
    void listRecipes("saved")
      .then((rs) => setCookbookCount(rs.length))
      .catch(() => setCookbookCount(0))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (auth.status !== "authed") return null

  const t = auth.me.targets
  const totals = data?.totals ?? {
    calories: 0,
    protein_g: 0,
    fiber_g: 0,
    carbs_g: 0,
    sugar_g: 0,
  }

  return (
    <Shell
      currentPath="/today"
      navItems={[
        { href: "/", label: "Dashboard" },
        { href: "/today", label: "Today" },
        { href: "/performance", label: "Performance" },
        { href: "/ingredients", label: "Ingredients" },
        { href: "/recipes", label: "Recipes" },
        { href: "/weight", label: "Weight" },
        { href: "/profile", label: "Profile" },
      ]}
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <p className="eyebrow mb-3 animate-ink-in">
          {new Date().toLocaleDateString(undefined, {
            weekday: "long",
            month: "long",
            day: "numeric",
          })}
        </p>
        <h1 className="font-display text-display tracking-tightest mb-12 animate-fade-up">
          What did you <span className="italic text-moss">eat today?</span>
        </h1>

        {!t ? (
          <div className="card-surface px-6 py-6 mb-12">
            <p className="font-sans text-base">
              Set up your profile first so we can compute targets.
            </p>
            <a
              href="/profile"
              className="inline-block mt-4 bg-foreground text-background px-5 py-2.5 rounded font-sans text-sm hover:bg-moss transition-colors"
            >
              Profile setup →
            </a>
          </div>
        ) : (
          <section className="mb-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-8 animate-fade-up" style={{ animationDelay: "120ms" }}>
            <MacroProgress label="calories" current={totals.calories} target={t.calories} unit="kcal" />
            <MacroProgress label="protein" current={totals.protein_g} target={t.protein_g} unit="g" />
            <MacroProgress label="fiber" current={totals.fiber_g} target={t.fiber_g} unit="g" />
            <MacroProgress label="carbs" current={totals.carbs_g} target={t.carbs_g} unit="g" />
            <MacroProgress label="sugar" current={totals.sugar_g} target={t.sugar_g} unit="g" tone="warn" />
          </section>
        )}

        <section className="mb-14">
          <MealComposer onLogged={onLogged} />
          {backfillNote && (
            <p className="mt-3 font-mono text-[11px] text-moss tracking-wider uppercase animate-fade-up">
              {backfillNote}
            </p>
          )}
        </section>

        <section>
          <p className="eyebrow mb-5">today's log</p>
          {loading ? (
            <p className="font-mono text-xs text-muted-foreground">loading…</p>
          ) : err ? (
            <p className="font-sans text-sm text-persimmon">{err}</p>
          ) : !data?.meals.length ? (
            // First-encounter empty state. If the user also has no cookbook
            // we explain the whole model; once they've started building
            // (or finished an empty day) it stays as a quiet one-liner.
            cookbookCount === 0 ? (
              <EmptyWithValueProp />
            ) : (
              <p className="font-sans text-sm text-muted-foreground">
                Nothing logged yet. Add one above — it's quick.
              </p>
            )
          ) : (
            <MealsByType meals={data.meals} onChanged={load} />
          )}
        </section>
      </div>
    </Shell>
  )
}

/**
 * EmptyWithValueProp — shown on a brand-new account's first visit to
 * Today when nothing has been logged AND no cookbook exists yet. The
 * goal is to make the FitTrack value-prop visible at the moment of first
 * use, not buried in a marketing page. Magazine voice — short, confident,
 * a clear next step.
 */
function EmptyWithValueProp() {
  return (
    <div className="card-surface px-6 sm:px-8 py-7 animate-fade-up">
      <p className="eyebrow mb-3">first time here</p>
      <h2 className="font-display text-2xl sm:text-3xl tracking-tightest leading-tight mb-3">
        Most apps make you log <span className="italic text-persimmon">every ingredient</span>.<br />
        FitTrack lets you log <span className="italic text-moss">dishes</span>.
      </h2>
      <p className="font-sans text-sm text-muted-foreground leading-relaxed max-w-xl mb-5">
        Build a dish once — from a recipe URL, a photo, or just text — and log
        it in a single tap next time you eat it. Macros for the whole plate,
        scaled to your portion. The cookbook is yours.
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <a
          href="/recipes/new"
          className="bg-foreground text-background px-5 py-2.5 rounded font-sans text-sm tracking-tight hover:bg-moss transition-colors"
        >
          Build your first dish →
        </a>
        <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider">
          or use the composer above for a one-off ingredient
        </span>
      </div>
    </div>
  )
}

function MealsByType({
  meals,
  onChanged,
}: {
  meals: MealsByDate["meals"]
  onChanged: () => Promise<void> | void
}) {
  const groups: Record<string, MealsByDate["meals"]> = {
    breakfast: [], lunch: [], dinner: [], snack: [],
  }
  for (const m of meals) groups[m.meal_type]?.push(m)
  return (
    <div className="space-y-8">
      {(["breakfast", "lunch", "dinner", "snack"] as const).map((type) => {
        const list = groups[type]
        if (!list.length) return null
        // Per-meal-type subtotal: every macro the row tracks, so the user
        // gets a per-meal carb (etc.) tally without per-meal targets — which
        // would force users into a meal-by-meal budgeting model they
        // probably don't want.
        const sub = list.reduce(
          (a, m) => ({
            cal: a.cal + m.calories,
            p: a.p + m.protein_g,
            f: a.f + m.fiber_g,
            c: a.c + m.carbs_g,
            s: a.s + m.sugar_g,
          }),
          { cal: 0, p: 0, f: 0, c: 0, s: 0 },
        )
        return (
          <div key={type}>
            <div className="flex items-baseline justify-between mb-2">
              <p className="eyebrow">{type}</p>
              <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
                {list.length} {list.length === 1 ? "item" : "items"}
              </p>
            </div>
            <p className="font-mono text-xs text-muted-foreground tabular-nums mb-3">
              <span className="text-foreground">{sub.cal.toFixed(0)} kcal</span>{" "}
              · {sub.p.toFixed(0)}g protein · {sub.c.toFixed(0)}g carbs ·{" "}
              {sub.f.toFixed(0)}g fiber · {sub.s.toFixed(0)}g sugar
            </p>
            <ul className="border-t border-b border-border divide-y divide-border">
              {list.map((m) => (
                <MealRowItem key={m.id} meal={m} onChanged={onChanged} />
              ))}
            </ul>
          </div>
        )
      })}
    </div>
  )
}

/**
 * MealRowItem — collapsed by default (header is a tappable button); on tap,
 * expands inline with quantity + meal-type editors + live macro re-preview +
 * save/delete. Editorial: hairline divider, eyebrows, animated chevron.
 *
 * Why inline (not modal): single-thumb mobile use; modal animations on
 * iOS Safari are jankier than CSS height transitions; and the surrounding
 * meal-type subtotals stay visible so the user can see the impact of an
 * edit on the parent group's macros after save.
 */
function MealRowItem({
  meal,
  onChanged,
}: {
  meal: MealRow
  onChanged: () => Promise<void> | void
}) {
  const [open, setOpen] = useState(false)
  const [qty, setQty] = useState<number>(meal.quantity_g)
  const [mt, setMt] = useState<MealRow["meal_type"]>(meal.meal_type)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const busyRef = useRef(false)

  // Reset local edit state if the row data changes from outside (e.g. another
  // device upserts) or when the row collapses without saving.
  useEffect(() => {
    if (!open) {
      setQty(meal.quantity_g)
      setMt(meal.meal_type)
      setErr(null)
    }
  }, [open, meal.quantity_g, meal.meal_type])

  // Live preview: scale macros by qty/quantity_g ratio (assumes per-row
  // macros are linear in quantity, which they are — they're computed
  // server-side as per100g * quantity / 100).
  const ratio = meal.quantity_g > 0 ? qty / meal.quantity_g : 0
  const preview = {
    cal: meal.calories * ratio,
    p: meal.protein_g * ratio,
    c: meal.carbs_g * ratio,
    f: meal.fiber_g * ratio,
    s: meal.sugar_g * ratio,
  }
  const dirty = qty !== meal.quantity_g || mt !== meal.meal_type

  async function save() {
    if (busyRef.current || !dirty) {
      setOpen(false)
      return
    }
    if (qty <= 0) {
      setErr("Quantity must be > 0.")
      return
    }
    busyRef.current = true
    setBusy(true)
    setErr(null)
    try {
      await patchMeal(meal.id, {
        quantity_g: qty !== meal.quantity_g ? qty : undefined,
        meal_type: mt !== meal.meal_type ? mt : undefined,
      })
      setOpen(false)
      await onChanged()
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || "Save failed.")
    } finally {
      setBusy(false)
      busyRef.current = false
    }
  }

  async function remove() {
    if (busyRef.current) return
    if (!confirm(`Remove "${meal.item_name}"?`)) return
    busyRef.current = true
    setBusy(true)
    try {
      await deleteMeal(meal.id)
      await onChanged()
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || "Delete failed.")
      busyRef.current = false
      setBusy(false)
    }
  }

  return (
    <li className={open ? "bg-muted/30" : ""}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left py-3 px-1 flex items-baseline justify-between gap-4 hover:bg-muted/40 transition-colors"
      >
        <div className="min-w-0">
          <p className="font-sans text-base truncate">{meal.item_name}</p>
          <p className="font-mono text-xs text-muted-foreground tabular-nums">
            {meal.quantity_g}g · {meal.calories.toFixed(0)} kcal ·{" "}
            {meal.protein_g.toFixed(1)}p · {meal.carbs_g.toFixed(1)}c ·{" "}
            {meal.fiber_g.toFixed(1)}f
          </p>
        </div>
        <span
          aria-hidden
          className={`font-mono text-xs text-muted-foreground transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        >
          ▾
        </span>
      </button>

      {open && (
        <div className="px-1 pb-5 pt-2 space-y-5 animate-fade-up">
          {/* Quantity + meal type */}
          <div className="grid grid-cols-[1fr_auto] gap-6">
            <label className="block">
              <span className="eyebrow block mb-2">quantity</span>
              <div className="flex items-baseline gap-2 border-b border-border focus-within:border-moss transition-colors py-1.5">
                <input
                  type="number"
                  inputMode="decimal"
                  value={Number.isFinite(qty) && qty > 0 ? qty : ""}
                  onChange={(e) => setQty(parseFloat(e.target.value) || 0)}
                  step={5}
                  min={0}
                  className="w-24 bg-transparent border-0 outline-none font-display text-3xl tracking-tight num text-foreground"
                />
                <span className="font-mono text-xs text-muted-foreground">
                  g
                </span>
              </div>
            </label>
            <div>
              <span className="eyebrow block mb-2">meal</span>
              <div className="flex flex-wrap gap-1">
                {(["breakfast", "lunch", "dinner", "snack"] as const).map(
                  (m) => {
                    const active = mt === m
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setMt(m)}
                        className={`px-2.5 py-1 rounded font-sans text-[11px] tracking-tight transition-colors ${
                          active
                            ? "bg-foreground text-background"
                            : "bg-surface border border-border text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {m}
                      </button>
                    )
                  },
                )}
              </div>
            </div>
          </div>

          {/* Live macro preview */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-2 text-xs">
            <MiniMacro label="kcal" before={meal.calories} after={preview.cal} />
            <MiniMacro label="protein" before={meal.protein_g} after={preview.p} unit="g" />
            <MiniMacro label="carbs" before={meal.carbs_g} after={preview.c} unit="g" />
            <MiniMacro label="fiber" before={meal.fiber_g} after={preview.f} unit="g" />
            <MiniMacro label="sugar" before={meal.sugar_g} after={preview.s} unit="g" tone="warn" />
          </div>

          {err && <p className="font-sans text-sm text-persimmon">{err}</p>}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="bg-foreground text-background px-5 py-2 rounded font-sans text-sm tracking-tight hover:bg-moss transition-colors disabled:opacity-50"
            >
              {busy ? "Saving…" : dirty ? "Save changes" : "Done"}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="eyebrow text-muted-foreground hover:text-foreground transition-colors"
            >
              cancel
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="ml-auto eyebrow text-muted-foreground hover:text-persimmon transition-colors"
            >
              delete
            </button>
          </div>
        </div>
      )}
    </li>
  )
}

function MiniMacro({
  label,
  before,
  after,
  unit = "",
  tone,
}: {
  label: string
  before: number
  after: number
  unit?: string
  tone?: "warn"
}) {
  const changed = Math.abs(before - after) > 0.05
  const numColor = tone === "warn" && after > before ? "text-persimmon" : "text-foreground"
  return (
    <div>
      <p className="eyebrow text-[10px] mb-0.5">{label}</p>
      <p className="font-mono tabular-nums">
        <span className={`text-base ${numColor}`}>
          {after.toFixed(label === "kcal" ? 0 : 1)}
        </span>
        {unit && (
          <span className="text-[10px] text-muted-foreground ml-0.5">
            {unit}
          </span>
        )}
        {changed && (
          <span className="block text-[10px] text-muted-foreground line-through tabular-nums">
            was {before.toFixed(label === "kcal" ? 0 : 1)}
            {unit}
          </span>
        )}
      </p>
    </div>
  )
}
